import { useTranslation } from "react-i18next"
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts"
import { type ProductDistributionChartProps } from "../../../types/analytics"
import { TruncatedText } from "../../ui/TruncatedText"

const COLORS = [
  "#3b82f6",
  "#8b5cf6",
  "#06b6d4",
  "#10b981",
  "#f59e0b",
  "#ef4444",
  "#8b5a3c",
  "#6b7280",
  "#ec4899",
  "#84cc16",
]

export const ProductDistributionChart: React.FC<ProductDistributionChartProps> = ({
  productAnalytics,
  showOverlay = false,
}) => {
  const { t } = useTranslation()
  const productTotals = productAnalytics.reduce((acc, item) => {
    const title =
      typeof item.product_title === "string" && item.product_title.trim().length > 0
        ? item.product_title
        : t("analytics.unknownProduct", "Unknown product")

    if (!acc[title]) acc[title] = 0

    acc[title] += item.vto_events
    return acc
  }, {} as Record<string, number>)

  const chartData = Object.entries(productTotals)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 8)

  const totalVtoEvents = chartData.reduce((sum, item) => sum + item.value, 0)
  const bestPerformingProduct = chartData.length > 0 ? chartData[0] : null

  interface TooltipPayloadItem {
    value: number
    payload: { name: string }
  }

  const activeProducts = chartData.length

  const CustomTooltip = ({
    active,
    payload,
  }: {
    active?: boolean
    payload?: TooltipPayloadItem[]
  }) => {
    if (active && payload && payload.length) {
      const data = payload[0]
      const percentage = ((data.value / totalVtoEvents) * 100).toFixed(1)
      return (
        <div className="bg-card p-3 rounded-lg border">
          <div className="font-semibold text-foreground">{data.payload.name}</div>
          <div className="text-sm text-foreground">
            {t("analytics.productDistribution.vtoEvents")}: {data.value.toLocaleString()}
          </div>
          <div className="text-sm text-muted-foreground">
            {t("analytics.productDistribution.share")}: {percentage}%
          </div>
        </div>
      )
    }
    return null
  }

  const CustomLegend = ({ payload }: { payload: Array<{ value: string; color: string }> }) => (
    <div className="max-h-[200px] overflow-y-auto">
      {payload?.map((entry, index) => (
        <div key={index} className="flex items-center gap-2 mb-1">
          <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: entry.color }} />
          <span className="text-xs text-muted-foreground">{entry.value}</span>
        </div>
      ))}
    </div>
  )

  if (showOverlay)
    return (
      <div className="p-4 bg-muted rounded min-h-[400px]">
        <h3 className="mb-2 text-lg font-semibold tracking-tight">
          {t("analytics.productDistribution.loading")}
        </h3>
        <div className="h-[300px] bg-muted/50 rounded" />
      </div>
    )

  if (chartData.length === 0)
    return (
      <div className="p-6 bg-card rounded-lg border h-full">
        <h3 className="text-lg font-semibold tracking-tight text-foreground">
          {t("analytics.productDistribution.title")}
        </h3>
        <div className="h-[300px] flex items-center justify-center">
          <p className="text-muted-foreground">{t("analytics.productDistribution.noData")}</p>
        </div>
      </div>
    )

  return (
    <div className="p-4 sm:p-6 bg-card rounded-lg border h-full overflow-hidden">
      <div className="flex flex-col gap-4 h-full">
        <div>
          <h3 className="text-lg font-semibold tracking-tight text-foreground">
            {t("analytics.productDistribution.title")}
          </h3>
          <p className="text-sm text-muted-foreground">
            {t("analytics.productDistribution.subtitle")}
          </p>
        </div>
        <div className="flex gap-3 sm:gap-6 flex-wrap">
          <div>
            <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {t("analytics.productDistribution.bestPerformer")}
            </div>
            <div className="text-base font-semibold leading-tight text-foreground sm:text-lg">
              <TruncatedText
                text={bestPerformingProduct?.name || "N/A"}
                maxLength={25}
                className="text-foreground"
              />
            </div>
            <div className="text-sm text-muted-foreground">
              {bestPerformingProduct?.value.toLocaleString() || 0}{" "}
              {t("analytics.productDistribution.vtoEvents")}
            </div>
          </div>

          <div>
            <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {t("analytics.productDistribution.activeProducts")}
            </div>
            <div className="text-xl font-semibold tracking-tight text-foreground">
              {activeProducts}
            </div>
            <div className="text-sm text-muted-foreground">
              {t("analytics.productDistribution.withActivity")}
            </div>
          </div>

          <div>
            <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {t("analytics.productDistribution.totalEvents")}
            </div>
            <div className="text-xl font-semibold tracking-tight text-foreground">
              {totalVtoEvents.toLocaleString()}
            </div>
            <div className="text-sm text-muted-foreground">
              {t("analytics.productDistribution.acrossAll")}
            </div>
          </div>
        </div>

        <div className="flex-1 min-h-[300px] sm:min-h-[350px]">
          <div className="flex flex-col sm:flex-row gap-4 h-full">
            <div className="flex-1 min-h-[250px] sm:min-h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={chartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={80}
                    dataKey="value"
                    stroke="#fff"
                    strokeWidth={2}
                  >
                    {chartData.map((_entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </div>

            <div className="w-full sm:min-w-[180px] sm:max-w-[250px]">
              <div className="mb-2 text-sm font-medium text-foreground">
                {t("analytics.productDistribution.topProducts")}
              </div>
              <CustomLegend
                payload={chartData.map((item, index) => ({
                  value: item.name,
                  color: COLORS[index % COLORS.length],
                }))}
              />
            </div>
          </div>
        </div>

        <div className="p-3 bg-muted rounded-lg border">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-0">
            <div>
              <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {t("analytics.productDistribution.bestPerformer")}
              </div>
              <div className="text-sm font-medium text-foreground">
                <TruncatedText
                  text={bestPerformingProduct?.name || "N/A"}
                  maxLength={25}
                  className="text-foreground"
                />
              </div>
            </div>
            <div className="text-center">
              <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {t("analytics.productDistribution.totalEvents")}
              </div>
              <div className="text-sm font-medium text-foreground">
                {totalVtoEvents.toLocaleString()}
              </div>
            </div>
            <div className="text-right">
              <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {t("analytics.productDistribution.activeProducts")}
              </div>
              <div className="text-sm font-medium text-foreground">{activeProducts}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
